import pluralize from 'pluralize'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import type { DocumentNode } from 'graphql'
import type { ClientHandle } from '@urql/vue'
import type { FilterOperation } from '../composables/simpleFilters'
import type Multiselect from '@vueform/multiselect'
import WSelect from '../components/w/Select.vue'
import WSelectProduct from '../components/w/SelectProduct.vue'


export type FormKitType =
  | 'channels'
  | 'channelGroup'
  | 'color'
  | 'date'
  | 'email'
  | 'group'
  | 'images'
  | 'upload'
  | 'password'
  | 'number'
  | 'radio'
  | 'repeater'
  | 'text'
  | 'textarea'
  | 'toggle'
  | 'select'
  | 'multiselect'
  | 'productSelect'
  | 'tagsSelect'
  | 'adapter'

interface IOptionValue {
  value: number
  label: string

  // for import types selection
  importDataType?: string
  name?: string
}

export abstract class BaseInput {
  abstract $formkit: FormKitType
  abstract name: string
  component?: any
  target?: string
  label?: string
  value?: any
  validation?: string
  placeholder?: string
  classes?: object
  attrs?: object
  disabled?: boolean | string
  if?: string
  id?: string
  props?: any
  help?: string
  mode? = 'single'
  watch?: string[]
  defaultStateValue?: any

  watchDisabled?: (state: any) => boolean

  constructor(args: Partial<BaseInput> = {}) {
    return Object.assign(this, args)
  }

  change<T=BaseInput>(args: Partial<T> = {}) {
    return Object.assign(this, args)
  }

  input() {
    return this
  }

  filter(attrs?: any) {
    return Object.assign(this, {
      // label: undefined,
      ...attrs,
    })
  }
}

export class BaseText extends BaseInput {
  $formkit: FormKitType = 'text'
  name: string
  constructor(private args: Partial<BaseText> = {}) {
    super(args)
    return Object.assign(this, args)
  }
}

export class BaseNumber extends BaseInput {
  $formkit: FormKitType = 'number'
  name: string

  constructor(args: Partial<BaseNumber> = {}) {
    super(args)
    return Object.assign(this, args)
  }
}

export class BaseDate extends BaseInput {
  $formkit: FormKitType = 'date'
  name: string
  override mode? = 'single'
  showTime? = false

  constructor(private args: Partial<BaseDate> = {}) {
    super(args)
    return Object.assign(this, args)
  }
}

export class BaseToggle extends BaseInput {
  $formkit: FormKitType = 'toggle'
  name: string
  constructor(args: Partial<BaseToggle> = {}) {
    super(args)
    return Object.assign(this, args)
  }
}

export class NumericInput extends BaseNumber {
  override name: string = 'numerical'
  override label = 'Numerical'
  override placeholder = 'Numerical'
  operation: FilterOperation

  constructor(private args: Partial<NumericInput> = {}) {
    super(args)
    return Object.assign(this, args)
  }

  override filter(attrs?: any) {
    return Object.assign(this, {
      ...super.filter(attrs),
      ...this.args,
    })
  }
}

export class BaseTagsSelect extends BaseNumber {
  override $formkit: FormKitType = 'tagsSelect';
  override mode = 'tags'  

  constructor(args: Partial<NumericInput>, options: { value: string; label: string }[]) {
    super(args)
    this.props = { options }
    // return Object.assign(this, args)
  }
}

export class BaseSelect extends BaseInput {
  $formkit: FormKitType = 'multiselect'
  // $formkit: FormKitType = 'adapter'
  name: string
  override mode? : Multiselect['$props']['mode'] = 'single'
  delay = 250
  filterResults = true
  keepValuesOnWatch?: boolean
  /**
   * Opt out of the route `?channel=` coercion applied in formkit/Adapter.vue.
   * Use for selects whose options are intentionally pinned to a fixed channel,
   * e.g. the product category pickers, where Backoffice edits channel 1.
   */
  ignoreRouteChannel?: boolean
  operation?: FilterOperation
  debounce?: boolean
  canClear?: boolean
  searchable?: boolean = true
  resolveOnLoad?: boolean = true
  object?: boolean = false
  clientHandle?: ClientHandle
  view?: string
  showAddAll?: boolean
  showLabelsWithIds?: boolean = false
  showLabelsWith?: string
  defaultTextPriority?: boolean = true
  availableFilters?: string[]
  initialPagination?: {
    perPage: number
  }
  nameValue?: string
  watcher?: ReturnType<typeof useWatcher>
  textsFallbackKey?: string
  public where?(context: any): any

  public initialOptions?(value: any, context?: any): Promise<any> | any

  public options(value: any, context: any): Promise<any[]> | any[] {
    return []
  }

  constructor(private args: Partial<BaseSelect> = {}) {
    super(args)
    this.clientHandle = useClientHandle()
    if (this.validation?.includes('required')) this.canClear = false
    const o = Object.assign(this, args)
    o.nameValue = o.name
    if (!o.props) {
      o.props = {}
    }
    // New Select
    return o.use(WSelect)
  }

  override filter(attrs?: any) {
    return Object.assign(this, {
      ...super.filter(attrs),
      ...this.getSchema(),
      ...this.args,
      where: this.where,
      nameValue: this.name
    })
  }

  legacy() {
    this.$formkit = 'multiselect'
    return this
  }

  products(view: 'full' | 'compact' = 'compact') {
    this.view = view
    return this.use(WSelectProduct)
  }

  adapted() {
    return this.use(WSelect).input()
  }

  override input() {
    return Object.assign(this, {
      ...super.input(),
      ...this.getSchema(),
      ...this.args,
      nameValue: this.name
    })
  }

  getSchema() {
    return {
      name: this.mode === 'single' ? this.name : pluralize(this.name),
      initialOptions: this.initialOptions?.bind(this),
      options: this.options.bind(this),
    }
  }

  public recordToLabel(item: any): string {
    const baseLabel =
      item.defaultText?.name ||
      (
        item[this.textsFallbackKey] 
        && item[this.textsFallbackKey] 
        && item[this.textsFallbackKey].length
        && item[this.textsFallbackKey][0].name
      ) ||
      item.name ||
      item.code
    
    let label = this.defaultTextPriority ? baseLabel : item.name || baseLabel
    if (!label) label = `${this.label} + ${item.id}`
    if (this.showLabelsWith) {
      label += ` (${item[this.showLabelsWith]})`
    }
    if (this.showLabelsWithIds) {
      const value = +item.id
      label += ` (${value})`
    }
    return label
  }

  protected recordToSelectItem(item: any) {
    const value = +item.id

    return {
      value,
      label: this.recordToLabel(item),
    }
  }

  sortSelectItems(items: any[]) {
    const sorted = items.sort((a, b) => {
      if (!a.disabled && b.disabled) return -1
      if (a.disabled && !b.disabled) return 1
      return 0
    })
    return sorted
  }

  async optionsQuery<T = any, V extends object = {}>(
    gqlQuery: string | DocumentNode | TypedDocumentNode<T, V>,
    field: string,
    options?: {
      variables?: V
      clientHandle?: ClientHandle
      mapData?: (data: T) => any
      plain?: boolean
    },
  ): Promise<IOptionValue[]> {
    const response = await this.clientHandle.client
      .query(gqlQuery, options?.variables, {
        requestPolicy: "network-only",
      })
      .toPromise()
    let { data } = response
    if (Array.isArray(data?.[field])) {
      data[field] = { data: data?.[field] }
    }

    if (options?.mapData) data = options.mapData(data || undefined)

    if (options?.plain) {
      return data?.[field]?.data || []
    }
    const items = data?.[field]?.data || []
    const res = this.sortSelectItems(items.map((item) => this.recordToSelectItem(item)))
    return res
  }

  use(component) {
    this.$formkit = "adapter"
    this.component = shallowRef(component)
    this.props.component = shallowRef(component)
    return this
  }

  show(state) {
    return true
  }

  hide(state) {
    return false
  }
}

export class OptimizedBaseSelect extends BaseSelect {
  protected cachedOptions: any = null
  protected lastQuery: string = ''
  override resolveOnLoad = false

  protected async cachedOptionsQuery<T = any, V extends object = {}>(
    query: string | DocumentNode | TypedDocumentNode<T, V>,
    field: string,
    options?: {
      variables?: V
      clientHandle?: ClientHandle
      mapData?: (data: T) => any
      plain?: boolean
    },
    cacheKey?: string,
  ) {
    const currentCacheKey = cacheKey || JSON.stringify(options?.variables)

    if (this.cachedOptions && this.lastQuery === currentCacheKey) {
      return this.cachedOptions
    }

    this.lastQuery = currentCacheKey
    const result = await this.optionsQuery(query, field, options)
    this.cachedOptions = result
    return result
  }
}

export const getOptions = <T extends number | string>(
  value: { value: T } | { value: T }[],
): T | T[] =>
  Array.isArray(value) ? value.map(({ value }) => value) : value?.value

export const watchesChannels = (v) => {
  if (v.watch || v.where)
    console.error("Can't watch via channel: fields are already defined", v)
  v.watch = ['channelId', 'channelIds']
  v.where = (state) => {
    if (state.channelIds?.length > 1) {
      return {
        channelId: {
          in: state.channelIds,
        },
      }
    }
    if (state.channelIds?.length === 1) {
      return {
        channelId: {
          equals: state.channelIds[0],
        },
      }
    }
    if (state.channelId) {
      return {
        channelId: {
          equals: state.channelId,
        },
      }
    }
    return {}
  }
  return v
}

export const conditionalField = (condition, schema) =>
  condition() ? schema : null
